import { ImportEntity, ImportJobStatus, type ImportJob, type ImportPreview } from '@paintscout/api'
import {
  Alert,
  Button,
  CircularProgress,
  LinearProgress,
  makeStyles,
  MenuItem,
  PopupMenu,
  Tooltip,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import GetAppIcon from '@material-ui/icons/GetApp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import classnames from 'classnames'
import FileSaver from 'file-saver'
import React from 'react'
import { buildJobErrorsCsv } from '../buildJobErrorsCsv'
import { countNoun } from '../countNoun'
import RowErrorsPanel from '../RowErrorsPanel'

/** Tooltip on the Skipped result tile (#24960). Moved here from the Review summary. */
export const SKIPPED_TOOLTIP = 'Updates with no actual changes are reported as skipped.'

/** Shown on the progress screen while polls cannot reach the server (#24991). The job is not failed. */
export const UNREACHABLE_COPY =
  "We can't reach PaintScout right now. Your import keeps running, and this screen updates when you're back online. You can close this window."

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  progress: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4, 0)
  },
  bar: {
    width: '100%'
  },
  muted: {
    color: theme.palette.text.secondary
  },
  // The terminal screens are centred; the running/error ones stay left-aligned.
  centered: {
    alignItems: 'center',
    textAlign: 'center'
  },
  // Heading and its count line read as one block, so they sit closer than the root gap.
  heading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  subtitle: {
    fontSize: '0.8125em',
    color: theme.paintscout.palette.brand.Neutral.P60
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(1.5),
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, 1fr)'
    }
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2, 1),
    borderRadius: theme.borderRadius.lg
  },
  // Each tile's number and label are darker shades of its own background hue, per the mock.
  tileCreated: { backgroundColor: theme.palette.success.light },
  tileUpdated: { backgroundColor: theme.palette.info.light },
  tileSkipped: { backgroundColor: theme.palette.warning.light },
  tileErrors: { backgroundColor: theme.palette.error.light },
  valueCreated: { color: theme.paintscout.palette.semantic.Success.S50 },
  valueUpdated: { color: theme.paintscout.palette.semantic.Info.S50 },
  valueSkipped: { color: theme.paintscout.palette.semantic.Warning.S50 },
  valueErrors: { color: theme.paintscout.palette.semantic.Danger.S50 },
  labelCreated: { color: theme.paintscout.palette.semantic.Success.S60 },
  labelUpdated: { color: theme.paintscout.palette.semantic.Info.S60 },
  labelSkipped: { color: theme.paintscout.palette.semantic.Warning.S60 },
  labelErrors: { color: theme.paintscout.palette.semantic.Danger.S60 },
  tileLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  infoIcon: {
    fontSize: 16
  },
  // The terminal screens carry their own buttons in the body rather than in a dialog footer.
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1)
  },
  // With errors, the "Download errors" menu sits on the left and the pair on the right (ENG-2559).
  actionsSplit: {
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    flexWrap: 'wrap'
  },
  actionGroup: {
    display: 'flex',
    gap: theme.spacing(1.5)
  },
  menuItem: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    whiteSpace: 'normal'
  },
  menuItemHelp: {
    color: theme.palette.text.secondary
  },
  fullWidth: {
    alignSelf: 'stretch'
  },
  // The shared Button `outlined` variant is a dashed, primary-blue placeholder; this is the plain
  // secondary button the mock shows. Same treatment as the header's Import button.
  secondaryAction: {
    '&&': {
      color: theme.paintscout.palette.brand.Neutral.P35,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: theme.paintscout.palette.brand.Neutral.P85
    },
    '&&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98
    }
  },
  downloads: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  }
}))

export interface ImportStepProps {
  entity: ImportEntity
  entityNoun: string
  entityNounPlural: string
  /** False for the background path: the wizard shows a confirmation and an email instead. */
  inlineEligible: boolean
  readyCount: number
  userEmail?: string
  job?: ImportJob
  preview?: ImportPreview
  starting: boolean
  errorMessage?: string
  onRetry: () => void
  /** The job's state cannot be fetched right now. A notice only: the job keeps running server-side. */
  unreachable?: boolean
  /** Label for the primary button on the terminal screens — "View contacts", "Done", etc. */
  doneLabel: string
  onImportAnother: () => void
  onDone: () => void
}

function pct(job?: ImportJob): number {
  if (!job?.totalRows) {
    return 0
  }
  return Math.min(100, Math.round((job.processedRows / job.totalRows) * 100))
}

/**
 * Step 4: run the import.
 *
 * At or below the server's inline row limit the job is polled and progress is shown live. Above
 * it the user gets a confirmation screen and an email when the job finishes — there is no polling.
 */
export default function ImportStep({
  entity,
  entityNoun,
  entityNounPlural,
  inlineEligible,
  readyCount,
  userEmail,
  job,
  preview,
  starting,
  errorMessage,
  onRetry,
  unreachable = false,
  doneLabel,
  onImportAnother,
  onDone
}: ImportStepProps) {
  const classes = useStyles()
  const isContact = entity === ImportEntity.Contact

  // Every screen the user can leave from carries the same pair. They live in the dialog body, not
  // in a DialogActions footer, because the mock puts them under the content with no rule above.
  const renderTerminalActions = (leading?: React.ReactNode) => (
    <div className={classnames(classes.actions, { [classes.actionsSplit]: !!leading })}>
      {leading}
      <div className={classes.actionGroup}>
        <Button variant="outlined" className={classes.secondaryAction} onClick={onImportAnother}>
          Import another file
        </Button>
        <Button variant="contained" onClick={onDone}>
          {doneLabel}
        </Button>
      </div>
    </div>
  )

  if (errorMessage) {
    return (
      <div className={classes.root}>
        <Alert severity="error">{errorMessage}</Alert>
        <div className={classes.downloads}>
          <Button variant="contained" onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // Background path: confirmation only, no polling and no progress bar.
  if (!inlineEligible && !starting) {
    return (
      <div className={classes.root} data-testid="import-background">
        <Typography variant="h3">Your import has started</Typography>
        <Typography variant="body1">
          We&apos;re importing {countNoun(readyCount, entityNoun, entityNounPlural)} in the background. You&apos;ll get
          an email at <strong>{userEmail}</strong> when it&apos;s complete. You can safely close this window.
        </Typography>
        {renderTerminalActions()}
      </div>
    )
  }

  const status = job?.status
  const isTerminal = status === ImportJobStatus.Complete || status === ImportJobStatus.Failed

  if (starting || !job || !isTerminal) {
    const isQueued = !job || status === ImportJobStatus.Queued

    return (
      <div className={classes.progress} data-testid="import-progress">
        {unreachable && (
          // The wrapper carries the test id: @paintscout/ui's Alert drops data-testid.
          <div className={classes.bar} data-testid="import-unreachable">
            <Alert severity="warning">{UNREACHABLE_COPY}</Alert>
          </div>
        )}
        <CircularProgress />
        <Typography variant="h4">Importing {entityNounPlural}...</Typography>
        <Typography variant="body2" className={classes.muted}>
          {isQueued ? 'Starting...' : `Processing ${job?.processedRows ?? 0} of ${job?.totalRows ?? 0}...`}
        </Typography>
        <LinearProgress className={classes.bar} variant="determinate" value={pct(job)} />
        <Typography variant="body2" className={classes.muted}>
          {pct(job)}%
        </Typography>
      </div>
    )
  }

  const errors = job.errors ?? []

  function handleDownloadJobErrors() {
    FileSaver.saveAs(
      new Blob([buildJobErrorsCsv(errors)], { type: 'text/csv;charset=utf-8' }),
      `${entity.toLowerCase()}-import-error-report.csv`
    )
  }

  function handleDownloadValidationErrors() {
    if (!preview?.errorRowsCsv) {
      return
    }
    FileSaver.saveAs(
      new Blob([preview.errorRowsCsv], { type: 'text/csv;charset=utf-8' }),
      `${entity.toLowerCase()}-import-errors.csv`
    )
  }

  // `String(...)` is load-bearing: Typography wraps its children in NumberFormat, which returns an
  // empty string for a falsy child, so a bare `{0}` renders nothing at all.
  const tiles = (
    <div className={classes.tiles} data-testid="import-result-tiles">
      <div className={classnames(classes.tile, classes.tileCreated)}>
        <Typography variant="h3" className={classes.valueCreated}>
          {String(job.created)}
        </Typography>
        <Typography variant="body2" className={classes.labelCreated}>
          Created
        </Typography>
      </div>
      <div className={classnames(classes.tile, classes.tileUpdated)}>
        <Typography variant="h3" className={classes.valueUpdated}>
          {String(job.updated)}
        </Typography>
        <Typography variant="body2" className={classes.labelUpdated}>
          Updated
        </Typography>
      </div>
      <div className={classnames(classes.tile, classes.tileSkipped)}>
        <Typography variant="h3" className={classes.valueSkipped}>
          {String(job.skipped)}
        </Typography>
        <span className={classes.tileLabel}>
          <Typography variant="body2" className={classes.labelSkipped}>
            Skipped
          </Typography>
          {/* The icon is focusable so keyboard users can open the tooltip (#24975's pattern).
              disableWrapper anchors the tooltip on the icon itself, with no Badge/div around it. */}
          <Tooltip title={SKIPPED_TOOLTIP} hideIcon disableWrapper>
            <InfoOutlinedIcon
              className={classnames(classes.infoIcon, classes.labelSkipped)}
              aria-label="About skipped"
              role="img"
              tabIndex={0}
            />
          </Tooltip>
        </span>
      </div>
      <div className={classnames(classes.tile, classes.tileErrors)}>
        <Typography variant="h3" className={classes.valueErrors}>
          {String(job.errorCount)}
        </Typography>
        <Typography variant="body2" className={classes.labelErrors}>
          Errors
        </Typography>
      </div>
    </div>
  )

  const errorSection = errors.length > 0 && (
    <>
      <div className={classes.fullWidth}>
        <RowErrorsPanel
          title={`${countNoun(job.errorCount, 'row', 'rows')} couldn't be imported`}
          errors={errors}
          data-testid="import-result-errors"
        />
      </div>
      {job.errorsTruncated && (
        <Typography variant="body2" className={classes.muted}>
          Showing the first 1,000 errors.
        </Typography>
      )}
    </>
  )

  // Only the non-zero parts, so "2 new companies created." reads as the mock shows it (#25164).
  const companyLinks = [
    job.companiesCreated > 0 && `${countNoun(job.companiesCreated, 'new company', 'new companies')} created`,
    job.companiesLinked > 0 && `${job.companiesLinked} matched existing`
  ].filter((part): part is string => !!part)

  // Both files in one menu (ENG-2559); the menu closes itself on a click.
  const downloadMenu =
    errors.length === 0 ? undefined : (
      <PopupMenu
        id="import-download-errors"
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        component={
          <Button
            variant="outlined"
            className={classes.secondaryAction}
            startIcon={<GetAppIcon />}
            endIcon={<ExpandMoreIcon />}
          >
            Download errors
          </Button>
        }
      >
        <MenuItem className={classes.menuItem} onClick={handleDownloadJobErrors}>
          <Typography variant="body1">Error report</Typography>
          <Typography variant="body2" className={classes.menuItemHelp}>
            Each failed row, its line and why.
          </Typography>
        </MenuItem>
        {preview?.errorRowsCsv && (
          <MenuItem className={classes.menuItem} onClick={handleDownloadValidationErrors}>
            <Typography variant="body1">Rows with validation errors</Typography>
            <Typography variant="body2" className={classes.menuItemHelp}>
              Your original rows, to fix and import again.
            </Typography>
          </MenuItem>
        )}
      </PopupMenu>
    )

  if (status === ImportJobStatus.Failed) {
    return (
      <div className={classes.root} data-testid="import-failed">
        <Alert severity="error">{job.failedReason ?? 'The import failed.'}</Alert>
        {tiles}
        <Typography variant="body2" className={classes.muted}>
          Rows already imported were saved. Fix the file and import the remaining rows.
        </Typography>
        {errorSection}
        {renderTerminalActions(downloadMenu)}
      </div>
    )
  }

  return (
    <div className={classnames(classes.root, classes.centered)} data-testid="import-complete">
      <div className={classes.heading}>
        <Typography variant="h3">Import complete</Typography>
        <Typography variant="body1" className={classes.subtitle}>
          {job.created} created, {job.updated} updated, {job.skipped} skipped,{' '}
          {countNoun(job.errorCount, 'error', 'errors')}.
        </Typography>
      </div>
      {tiles}
      {isContact && companyLinks.length > 0 && (
        <Typography variant="body2" className={classes.subtitle} data-testid="import-company-links">
          Contacts were linked to the companies in your file: {companyLinks.join(', ')}.
        </Typography>
      )}
      {errorSection}
      {renderTerminalActions(downloadMenu)}
    </div>
  )
}
